import { useNavigate, useLocation } from 'react-router-dom'
import { FolderOpen, FolderKanban, ClipboardList, Package, Monitor, FolderTree } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useProjectContext } from '@/lib/context/ProjectContext'

interface TestsLayoutProps {
    children: React.ReactNode
}

// The Projects tab must always render — it's how users create and navigate
// to projects. All other tabs require a project to be selected.
const PROJECT_REQUIRED_TABS = new Set([
    'test-cases',
    'test-suites',
    'test-plans',
    'builds',
    'platforms',
])

export default function TestsLayout({ children }: TestsLayoutProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { selectedProject } = useProjectContext()

    const getActiveTab = () => {
        if (location.pathname.includes('/tests/projects')) return 'projects'
        if (location.pathname.includes('/tests/test-cases')) return 'test-cases'
        if (location.pathname.includes('/tests/test-suites')) return 'test-suites'
        if (location.pathname.includes('/tests/test-plans')) return 'test-plans'
        if (location.pathname.includes('/tests/builds')) return 'builds'
        if (location.pathname.includes('/tests/platforms')) return 'platforms'
        return 'projects'
    }

    const handleTabChange = (value: string) => {
        navigate(`/tests/${value}`)
    }

    const activeTab = getActiveTab()
    const requiresProject = PROJECT_REQUIRED_TABS.has(activeTab)

    return (
        <div className="p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Test Ops</h1>
                <p className="text-muted-foreground mt-1">
                    {selectedProject ? (
                        <>
                            Test cases, suites, and plans for{' '}
                            <span className="font-medium text-foreground">{selectedProject.name}</span>
                        </>
                    ) : (
                        'Manage your test projects, cases, suites, plans, builds, and platforms'
                    )}
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full max-w-5xl grid-cols-6">
                    <TabsTrigger value="projects">
                        <FolderOpen className="h-4 w-4 mr-2" />
                        Projects
                    </TabsTrigger>
                    <TabsTrigger value="test-cases">
                        <FolderKanban className="h-4 w-4 mr-2" />
                        Test Cases
                    </TabsTrigger>
                    <TabsTrigger value="test-suites">
                        <FolderTree className="h-4 w-4 mr-2" />
                        Test Suites
                    </TabsTrigger>
                    <TabsTrigger value="test-plans">
                        <ClipboardList className="h-4 w-4 mr-2" />
                        Test Plans
                    </TabsTrigger>
                    <TabsTrigger value="builds">
                        <Package className="h-4 w-4 mr-2" />
                        Builds
                    </TabsTrigger>
                    <TabsTrigger value="platforms">
                        <Monitor className="h-4 w-4 mr-2" />
                        Platforms
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {requiresProject && !selectedProject ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20">
                    <FolderOpen className="h-12 w-12 text-gray-300" />
                    <h3 className="mt-4 text-lg font-semibold text-gray-600">No Project Selected</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Select a project from the header to continue.
                    </p>
                </div>
            ) : (
                children
            )}
        </div>
    )
}